import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardApi, useGetAhuUnitQuery } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { openDuplicateAccessoryPopup, selectSection } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuPositionRow } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { formatTunnelAddress } from '../addressing';
import { duplicateAccessoryAny } from '../boardActions';
import { ACCESSORY_PLURALS } from './tabs/TabAccessories';

/**
 * `component_specific_accessories` (toolbar.components.js:45) — the only two
 * accessory types whose target list is filtered by section type. For anything
 * else legacy's `csa_available_sections[type]` is undefined and every section
 * of every tunnel is offered, which is why this gate is narrow by design.
 */
const CSA_TYPES = ['drain_pan', 'uvc'];

/**
 * Phase 6e1 — Duplicate Accessory popup (§12.1, duplicate_accessory.js +
 * duplicate.accessory.tmpl, markup faithful). Opened via the §13 row 12 seam
 * (popupSeams.duplicateAccessoryId — context menu / toolbar copy li / row
 * anchor all write it). Loads the accessory + position (mounting gating:
 * plane 9 → none; >9 → int-wall mode forces mounting-off when the target
 * section differs), tunnel rows with per-tunnel section selects, DUPLICATE →
 * `duplicate:accessory:any` (api §9.3 receiver).
 */

interface DuplicateAccessoryPopupProps {
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
}

const DuplicateAccessoryPopup = ({ tunnels, sections }: DuplicateAccessoryPopupProps) => {
  const dispatch = useAppDispatch();
  const { ahuId: ahuIdParam } = useParams();
  const ahuId = Number(ahuIdParam);
  const accessoryId = useAppSelector((s) => s.ahuBoard.popupSeams.duplicateAccessoryId);
  const [accessoryRow, setAccessoryRow] = useState<Record<string, unknown> | null>(null);
  const [model, setModel] = useState<Record<string, unknown> | null>(null);
  const [position, setPosition] = useState<IAhuPositionRow | null>(null);
  const [includeMounting, setIncludeMounting] = useState(true);
  const [selectedTunnelId, setSelectedTunnelId] = useState<number | null>(null);
  const [selectedSectionByTunnel, setSelectedSectionByTunnel] = useState<
    Record<number, number>
  >({});
  /** `csa_available_sections[type]` section-type names, null when ungated. */
  const [availableSections, setAvailableSections] = useState<string[] | null>(null);
  const { data: ahuUnit } = useGetAhuUnitQuery(Number.isFinite(ahuId) ? ahuId : skipToken);

  const sourceSection = useMemo(
    () =>
      sections.find((s) => s.id === Number((accessoryRow as Record<string, unknown>)?.section_id)),
    [sections, accessoryRow]
  );

  // Load source rows when the seam opens.
  useEffect(() => {
    if (accessoryId == null) {
      setAccessoryRow(null);
      setModel(null);
      setPosition(null);
      setIncludeMounting(true);
      // Legacy builds a fresh view per open, so the per-tunnel section picks and
      // the csa filter must not survive into the next accessory's popup.
      setSelectedSectionByTunnel({});
      setAvailableSections(null);
      return undefined;
    }
    let cancelled = false;
    // The generic accessory row comes from any cached section list.
    let row: Record<string, unknown> | null = null;
    for (const section of sections) {
      const sub = dispatch(
        ahuBoardApi.endpoints.getSectionAccessories.initiate(section.id)
      );
      // synchronous cache read via unwrap (resolves immediately when cached).
      sub
        .unwrap()
        .then((rows) => {
          if (cancelled || row) return;
          const hit = rows.find((r) => r.id === accessoryId);
          if (hit) {
            row = { ...hit };
            setAccessoryRow(row);
            setSelectedTunnelId(
              sections.find((s) => s.id === hit.section_id)?.tunnel_id ?? null
            );
            const plural = ACCESSORY_PLURALS[String(hit.type ?? '')];
            if (plural) {
              dispatch(
                ahuBoardApi.endpoints.getAccessoryDetail.initiate(
                  { plural, id: accessoryId },
                  { forceRefetch: true }
                )
              )
                .unwrap()
                .then((detail) => {
                  if (!cancelled) setModel(detail as Record<string, unknown>);
                })
                .catch(() => undefined);
            }
            dispatch(
              ahuBoardApi.endpoints.getAccessoryPosition.initiate(accessoryId, {
                forceRefetch: true,
              })
            )
              .unwrap()
              .then((pos) => {
                if (cancelled) return;
                // plane 9 → none (§12.1).
                if (pos?.mounting_plane_id === 9) {
                  setPosition(null);
                  setIncludeMounting(false);
                } else {
                  setPosition(pos);
                }
              })
              .catch(() => {
                if (!cancelled) {
                  setPosition(null);
                  setIncludeMounting(false);
                }
              });
          }
        })
        .catch(() => undefined);
      sub.unsubscribe();
    }
    return () => {
      cancelled = true;
    };
  }, [accessoryId, sections, dispatch]);

  // `csa_available_sections` gate (duplicate_accessory.js:49-53) — a drain pan or
  // a UVC may only be duplicated into a section type the CMS allows it in, so
  // the target list is filtered and a tunnel with no eligible section goes
  // non-clickable. Legacy reads the collections the insert toolbar had already
  // fetched; here it is the same endpoint InsertStrip uses, and the RTK cache
  // makes the second read free.
  const accessoryType = String(accessoryRow?.type ?? '');
  useEffect(() => {
    if (!CSA_TYPES.includes(accessoryType)) {
      setAvailableSections(null);
      return undefined;
    }
    let cancelled = false;
    dispatch(
      ahuBoardFormsApi.endpoints.getAccessoryAvailableSections.initiate({
        name: accessoryType,
        constructionClassId: ahuUnit?.construction_class_id ?? 1,
      })
    )
      .unwrap()
      .then((rows) => {
        if (cancelled) return;
        // legacy: `el.get('section_type').replace('split_', '')` — a split
        // plenum's rows come back as `split_plenum` but the section's own `type`
        // is `plenum`, and `String.replace` with a string pattern is
        // first-occurrence only, so this is a prefix strip.
        setAvailableSections(
          rows.map((r) => String(r.section_type ?? '').replace('split_', ''))
        );
      })
      .catch(() => {
        // Legacy never resolves its Promise.all on a failed fetch, so the popup
        // simply has no filter; match that rather than blanking the tunnel list.
        if (!cancelled) setAvailableSections(null);
      });
    return () => {
      cancelled = true;
    };
  }, [accessoryType, ahuUnit?.construction_class_id, dispatch]);

  // Escape = cancel. Dropping `active` (see the class-list note on the wrapper)
  // takes this popup out of usePopupKeys' `.popup-wrapper.active` scope, which
  // is legacy's behaviour — main.js:332 gates its Esc handler on the same
  // selector, so Esc did nothing here on the old board. Keep the Esc-cancels
  // semantics anyway: it was added deliberately (tracker "Esc semantics" item ⑦,
  // where Esc had been CREATING a duplicate by falling through to the primary
  // button), and InsertSectionPopup carries its own handler for the same reason.
  useEffect(() => {
    if (accessoryId == null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch(openDuplicateAccessoryPopup(null));
    };
    document.addEventListener('keyup', onKey);
    return () => document.removeEventListener('keyup', onKey);
  }, [accessoryId, dispatch]);

  /** A tunnel's offered sections, after the csa filter (empty ⇒ tunnel gated). */
  const eligibleSections = (tunnelId: number): IAhuBoardSection[] => {
    const list = sections.filter((s) => s.tunnel_id === tunnelId);
    if (!availableSections) return list;
    return list.filter((s) => availableSections.includes(String(s.type ?? '')));
  };

  /**
   * What the tunnel's `<select>` shows. Legacy renders the options and marks
   * `selected` on the one whose id is `selected_section` — the SOURCE section —
   * leaving the browser's own default (the FIRST option) for every other tunnel,
   * and `duplicateAccessory` then reads the select back.
   *
   * The port defaulted to `list[list.length - 1]` instead, so pressing DUPLICATE
   * without touching anything put the copy in the tunnel's LAST section rather
   * than beside the original. On a one-section tunnel the two coincide, which is
   * why it survived review.
   */
  const targetSectionId = (tunnelId: number): number | null => {
    const list = eligibleSections(tunnelId);
    if (!list.length) return null;
    const picked = selectedSectionByTunnel[tunnelId];
    if (picked != null && list.some((s) => s.id === picked)) return picked;
    const source = list.find((s) => s.id === sourceSection?.id);
    return (source ?? list[0]).id;
  };

  const isIntWall = (position?.mounting_plane_id ?? 0) > 9;
  const activeTunnelId = selectedTunnelId ?? sourceSection?.tunnel_id ?? null;
  const activeTargetId = activeTunnelId == null ? null : targetSectionId(activeTunnelId);
  // Only an INT-WALL mounting is section-bound, so `checkSection`
  // (duplicate_accessory.js:139-150) clears and locks Include Mounting when the
  // target moves off the source section — and re-arms it on the way back, which
  // is why this is an effect rather than a pure derivation.
  const mountingLocked = isIntWall && activeTargetId !== sourceSection?.id;
  const mountingAvailable = !!position && !mountingLocked;
  useEffect(() => {
    if (isIntWall) setIncludeMounting(!mountingLocked);
  }, [isIntWall, mountingLocked]);

  if (accessoryId == null || !accessoryRow) return null;

  const type = String(accessoryRow.type ?? '');
  // duplicate.accessory.tmpl:6 prints `{{accessory.name}}` — the generic row's
  // display name ('Hood', 'Access door', 'ADP Gage'), not the raw type. Same
  // fallback chain TabAccessories' `displayName` uses.
  const name = String(accessoryRow.name ?? accessoryRow.label ?? type);

  const close = () => dispatch(openDuplicateAccessoryPopup(null));

  const doDuplicate = (tunnelIdOverride?: number) => {
    const tunnelId = tunnelIdOverride ?? activeTunnelId;
    if (tunnelId == null) return;
    const sectionId = targetSectionId(tunnelId);
    if (sectionId == null || !model) return;
    const sameSection = sectionId === sourceSection?.id;
    // Int-wall positions force mounting-off when the target differs (§12.1).
    const effectiveMounting = includeMounting && !(isIntWall && !sameSection);
    if (!sameSection) {
      dispatch(selectSection({ sectionId }));
      ahuBoardBus.emit('select:section', { section: { id: sectionId } });
      ahuBoardBus.emit('force:select:section', sectionId);
    }
    void dispatch(
      duplicateAccessoryAny(
        sectionId,
        type,
        model,
        effectiveMounting && position ? ({ ...position } as Record<string, unknown>) : null,
        sameSection,
        tunnelId
      )
    );
    close();
  };

  return (
    // Class list verbatim from duplicate.accessory.tmpl:1 — in particular NO
    // `active`, and duplicate_accessory.js never adds it (its only addClass is
    // `active` on a `.tunnel-row`). The popup is visible without it:
    // ahu/styles.css:6642 `.location-popup.tunnel-popup { display: block
    // !important }` outranks common.css:27546 `.popup-wrapper { display: none
    // !important }` on specificity, which is why legacy's template never set it.
    //
    // Adding `active` switched on common.css:27561
    // `.popup-wrapper.active .popup-content { animation-name: easePopup }`, and
    // easePopup (:28243) is `scale(0,0) → scale(1,1)` carrying NO translate — so
    // for its 0.3s it overrode the static `transform: translate(-50%, -30%)`
    // that centres this dialog (ahu/styles.css:6645). The box is laid out from
    // its top-left corner and then snaps to centre when the animation ends,
    // which is the drag/slide the report describes. Same defect, same fix as the
    // Add Section dialog (c49d921680).
    //
    // The dialog is not left un-animated, though: ahuBoardOverrides.scss §10
    // gives `.duplicate-content` the Delete Accessory popup's scale-in with the
    // centring translate carried through the keyframes (requested 2026-09-08).
    // Keep that on the CONTENT class — re-adding `active` here would switch
    // easePopup back on and reintroduce the snap.
    <div className="popup-wrapper tunnel-popup location-popup">
      <div className="popup-content popup-content-animation duplicate-content">
        <h2 className="popup-heading">Duplicate Accessory</h2>
        <div className="popup-content-part accss-section">
          <div className="accessory_info">
            <span className="w-200">Accessory to Duplicate</span>
            <span className="accessory-name">
              {name}
              {accessoryRow.tag ? ` : ${String(accessoryRow.tag)}` : ''}
            </span>
          </div>
          <div className="section_info">
            <span className="w-200">Duplicate from Section</span>
            <span>
              {String(sourceSection?.name ?? '')}
              {sourceSection?.tag ? ` : ${sourceSection.tag}` : ''}
            </span>
          </div>
        </div>
        <div className="popup-content-part">
          <div className="duplicate-popup-content">
            <div className="d-flex">
              <div className="first_column">Duplicate into Section</div>
              <div
                className={`position-checkbox-wrapper${
                  mountingAvailable ? '' : ' non-clickable'
                }`}
              >
                <input
                  type="checkbox"
                  className="duplicate-mounting"
                  checked={mountingAvailable && includeMounting}
                  onChange={(e) => setIncludeMounting(e.currentTarget.checked)}
                />
                Include Mounting
              </div>
            </div>
            <div>
              {tunnels.map((tunnel) => {
                // filterTunnelSections (duplicate_accessory.js:81-108): a tunnel
                // with no eligible section is non-clickable but still lists ALL
                // of its sections; otherwise one section renders as a bare span
                // and two or more as the select.
                const eligible = eligibleSections(tunnel.id);
                const disable = !eligible.length;
                const list = disable
                  ? sections.filter((s) => s.tunnel_id === tunnel.id)
                  : eligible;
                const onlyOne = !disable && list.length === 1 ? list[0] : null;
                return (
                  <div
                    key={tunnel.id}
                    className={`tunnel-row${
                      activeTunnelId === tunnel.id ? ' active' : ''
                    }${disable ? ' non-clickable' : ''}`}
                    data-id={tunnel.id}
                    onClick={() => !disable && setSelectedTunnelId(tunnel.id)}
                    // `"dblclick .tunnel-row": "duplicateAccessory"` — double
                    // clicking a tunnel picks it AND commits, so the tunnel id
                    // goes in explicitly rather than through the click's
                    // not-yet-committed state.
                    onDoubleClick={() => !disable && doDuplicate(tunnel.id)}
                  >
                    <div className="tunnel-index">
                      {ahuUnit?.is_hybrid ? formatTunnelAddress(tunnel, true) : tunnel.index}
                    </div>
                    <div className="sections-list">
                      {onlyOne ? (
                        <span data-id={onlyOne.id} className="only_one">
                          {String(onlyOne.name ?? '')}
                        </span>
                      ) : (
                        <select
                          className="tunnel-section-select"
                          value={targetSectionId(tunnel.id) ?? list[0]?.id ?? ''}
                          onChange={(e) => {
                            // Read the value before the updater: React nulls
                            // `currentTarget` once the handler returns, and the
                            // updater runs later.
                            const sectionId = Number(e.currentTarget.value);
                            setSelectedSectionByTunnel((prev) => ({
                              ...prev,
                              [tunnel.id]: sectionId,
                            }));
                          }}
                        >
                          {list.map((s) => (
                            <option key={s.id} value={s.id}>
                              {String(s.name ?? '')}
                              {s.tag ? ` : ${s.tag}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="popup-control-buttons">
          <button className="btn duplicate" autoFocus onClick={() => doDuplicate()}>
            DUPLICATE
          </button>
          {/* name="cancel" kept as the usePopupKeys Esc target for the blanket
              paths that still scan for it; the local keyup handler above is what
              actually cancels this popup now that the wrapper has no `active`. */}
          <button className="btn cancel" name="cancel" onClick={close}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateAccessoryPopup;
